/**
 * Logo Carousel Frontend JavaScript
 * Based on WAI-ARIA Carousel Pattern (prev/next)
 * @see https://www.w3.org/WAI/ARIA/apg/patterns/carousel/examples/carousel-1-prev-next/
 */

class NPILogoCarousel {
	constructor( element ) {
		this.carousel = element;
		this.viewport = this.carousel.querySelector( '.npi-logo-carousel-viewport' );
		this.track = this.carousel.querySelector( '.npi-logo-carousel-track' );
		this.items = Array.from( this.track.querySelectorAll( '.npi-logo-carousel-item' ) );
		this.totalItems = this.items.length;
		this.visibleCount = parseInt( this.carousel.dataset.visibleCount ) || 4;
		this.autoPlay = this.carousel.dataset.autoPlay === 'true';
		this.autoPlayInterval = parseInt( this.carousel.dataset.autoPlayInterval ) || 3000;
		this.currentIndex = 0;
		this.autoPlayTimer = null;
		this.isPlaying = false;

		if ( this.totalItems <= 1 ) {
			return;
		}

		this.init();
	}

	init() {
		this.createControls();
		this.updateSlideVisibility();
		this.setupEventListeners();

		if ( this.autoPlay ) {
			this.startAutoPlay();
		}
	}

	createControls() {
		if ( ! this.autoPlay ) {
			return;
		}

		const controlsContainer = document.createElement( 'div' );
		controlsContainer.className = 'npi-logo-carousel-controls';

		// Auto-play toggle (only control since prev/next removed)
		this.playPauseButton = document.createElement( 'button' );
		this.playPauseButton.className = 'npi-logo-carousel-btn npi-logo-carousel-btn--pause';
		this.playPauseButton.type = 'button';
		this.updatePlayPauseButton();
		controlsContainer.appendChild( this.playPauseButton );

		// Insert controls after the viewport
		this.viewport.parentNode.appendChild( controlsContainer );
	}

	generateTrackId() {
		const id = 'npi-logo-carousel-track-' + Math.random().toString( 36 ).substring( 2, 9 );
		this.track.id = id;
		return id;
	}

	setupEventListeners() {
		if ( this.playPauseButton ) {
			this.playPauseButton.addEventListener( 'click', () => this.toggleAutoPlay() );
		}

		// Keyboard navigation
		this.carousel.addEventListener( 'keydown', ( e ) => this.handleKeydown( e ) );

		// Pause auto-play on hover/focus per ARIA pattern
		if ( this.autoPlay ) {
			this.carousel.addEventListener( 'mouseenter', () => this.pauseAutoPlay() );
			this.carousel.addEventListener( 'mouseleave', () => this.resumeAutoPlay() );
			this.carousel.addEventListener( 'focusin', () => this.pauseAutoPlay() );
			this.carousel.addEventListener( 'focusout', () => this.resumeAutoPlay() );
		}

		// Respect prefers-reduced-motion
		const motionQuery = window.matchMedia( '(prefers-reduced-motion: reduce)' );
		if ( motionQuery.matches ) {
			this.stopAutoPlay();
		}
		motionQuery.addEventListener( 'change', ( e ) => {
			if ( e.matches ) {
				this.stopAutoPlay();
			}
		} );
	}

	handleKeydown( event ) {
		switch ( event.key ) {
			case 'ArrowLeft':
				event.preventDefault();
				this.prev();
				break;
			case 'ArrowRight':
				event.preventDefault();
				this.next();
				break;
		}
	}

	getMaxIndex() {
		const step = this.getStepSize();
		if ( step > 1 ) {
			// Snap to page boundaries so we never land mid-column
			return ( Math.ceil( this.totalItems / step ) - 1 ) * step;
		}
		return Math.max( 0, this.totalItems - this.getVisibleCount() );
	}

	prev() {
		const step = this.getStepSize();
		if ( this.currentIndex > 0 ) {
			this.currentIndex = Math.max( 0, this.currentIndex - step );
		} else {
			this.currentIndex = this.getMaxIndex();
		}
		this.updateSlidePosition();
		this.updateSlideVisibility();
	}

	next() {
		const step = this.getStepSize();
		const maxIndex = this.getMaxIndex();
		if ( this.currentIndex < maxIndex ) {
			this.currentIndex = Math.min( maxIndex, this.currentIndex + step );
		} else {
			this.currentIndex = 0;
		}
		this.updateSlidePosition();
		this.updateSlideVisibility();
	}

	isMobileGrid() {
		return window.innerWidth < 480;
	}

	getStepSize() {
		// At mobile, step by a full page (4 items in 2x2 grid)
		return this.isMobileGrid() ? 4 : 1;
	}

	getVisibleCount() {
		// Responsive: fewer items on smaller screens
		const width = window.innerWidth;
		if ( width < 480 ) {
			return 4; // 2x2 grid
		}
		if ( width < 768 ) {
			return 2;
		}
		if ( width < 960 ) {
			return Math.min( 3, this.visibleCount );
		}
		return this.visibleCount;
	}

	updateSlidePosition() {
		if ( this.isMobileGrid() ) {
			// Grid has 2 rows, so each column holds 2 items.
			// One "page" = 2 columns = 100% of viewport width.
			const columnIndex = this.currentIndex / 2;
			const offset = -( columnIndex * 50 );
			this.track.style.transform = `translateX(${ offset }%)`;
		} else {
			const itemWidthPercent = 100 / this.getVisibleCount();
			const offset = -( this.currentIndex * itemWidthPercent );
			this.track.style.transform = `translateX(${ offset }%)`;
		}
	}

	updateSlideVisibility() {
		const visible = this.getVisibleCount();
		this.items.forEach( ( item, index ) => {
			const isVisible = index >= this.currentIndex && index < this.currentIndex + visible;
			item.setAttribute( 'aria-hidden', isVisible ? 'false' : 'true' );
			item.inert = ! isVisible;
		} );

		// At mobile the CSS grid handles sizing; otherwise set flex widths
		if ( ! this.isMobileGrid() ) {
			const itemWidthPercent = 100 / visible;
			this.items.forEach( ( item ) => {
				item.style.flex = `0 0 ${ itemWidthPercent }%`;
			} );
		} else {
			this.items.forEach( ( item ) => {
				item.style.flex = '';
			} );
		}

		// Update live region
		this.updateLiveRegion();

		// Update aria-live based on auto-play state
		this.track.setAttribute( 'aria-live', this.isPlaying ? 'off' : 'polite' );
	}

	updateLiveRegion() {
		if ( ! this.liveRegion ) {
			this.liveRegion = document.createElement( 'div' );
			this.liveRegion.setAttribute( 'aria-live', 'polite' );
			this.liveRegion.setAttribute( 'aria-atomic', 'true' );
			this.liveRegion.className = 'screen-reader-text';
			this.carousel.appendChild( this.liveRegion );
		}

		const visible = this.getVisibleCount();
		const start = this.currentIndex + 1;
		const end = Math.min( this.currentIndex + visible, this.totalItems );
		this.liveRegion.textContent = `Showing logos ${ start } through ${ end } of ${ this.totalItems }`;
	}

	startAutoPlay() {
		if ( this.totalItems <= this.getVisibleCount() ) {
			return;
		}
		this.isPlaying = true;
		this.autoPlayTimer = setInterval( () => this.next(), this.autoPlayInterval );
		this.updatePlayPauseButton();
		this.track.setAttribute( 'aria-live', 'off' );
	}

	stopAutoPlay() {
		clearInterval( this.autoPlayTimer );
		this.autoPlayTimer = null;
		this.isPlaying = false;
		this.updatePlayPauseButton();
		this.track.setAttribute( 'aria-live', 'polite' );
	}

	toggleAutoPlay() {
		if ( this.isPlaying ) {
			this.stopAutoPlay();
		} else {
			this.startAutoPlay();
		}
	}

	pauseAutoPlay() {
		if ( this.autoPlayTimer ) {
			clearInterval( this.autoPlayTimer );
			this.autoPlayTimer = null;
		}
	}

	resumeAutoPlay() {
		if ( this.autoPlay && this.isPlaying && ! this.autoPlayTimer ) {
			this.autoPlayTimer = setInterval( () => this.next(), this.autoPlayInterval );
		}
	}

	updatePlayPauseButton() {
		if ( ! this.playPauseButton ) {
			return;
		}
		this.playPauseButton.setAttribute(
			'aria-label',
			this.isPlaying ? 'Stop automatic slide show' : 'Start automatic slide show'
		);
		this.playPauseButton.innerHTML = this.isPlaying
			? '<span class="screen-reader-text">Pause</span><span aria-hidden="true">\u23F8</span>'
			: '<span class="screen-reader-text">Play</span><span aria-hidden="true">\u25B6</span>';
	}
}

// Initialize
document.addEventListener( 'DOMContentLoaded', () => {
	document.querySelectorAll( '.npi-logo-carousel' ).forEach( ( el ) => {
		new NPILogoCarousel( el );
	} );
} );
